import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const INDEX_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/return_decomposition_index.json";

const SHARDS_BASE_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/return_decomposition_shards";

function formatPercent(v, digits = 2) {
  const num = Number(v);
  if (!Number.isFinite(num)) return "—";
  return `${(num ).toFixed(digits)}%`;
}

function getColor(v) {
  const num = Number(v);
  if (!Number.isFinite(num)) return "#999";
  if (num > 0) return "#15803d";
  if (num < 0) return "#b91c1c";
  return "#555";
}

const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontWeight: 700,
  fontSize: 12,
  color: "#666",
  background: "#fafafa",
  borderBottom: "1px solid #eee",
};

const tdStyle = {
  padding: "10px 12px",
  borderTop: "1px solid #eee",
  fontSize: 13,
};

export default function StockReturnDecomposition() {
  const params = useParams();
  const ticker = String(params.symbol || params.ticker || params.id || "")
    .trim()
    .toUpperCase();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function fetchJson(url) {
      const res = await fetch(url, { cache: "no-store" });
      const text = await res.text();

      if (!res.ok) {
        throw new Error(`Failed to load ${url} (${res.status})`);
      }

      const trimmed = text.trim();
      if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
        throw new Error(`Expected JSON but got non-JSON response from ${url}`);
      }

      return JSON.parse(text);
    }

    async function load() {
      try {
        setLoading(true);
        setError("");
        setRows([]);

        if (!ticker) {
          if (!cancelled) setLoading(false);
          return;
        }

        const indexJson = await fetchJson(INDEX_URL);
        const shardFile = indexJson?.[ticker];

        if (!shardFile) {
          if (!cancelled) {
            setRows([]);
            setLoading(false);
          }
          return;
        }

        const shardUrl = `${SHARDS_BASE_URL}/${shardFile}`;
        const shardJson = await fetchJson(shardUrl);

        const tickerBlock = Array.isArray(shardJson)
          ? shardJson.find((x) => String(x?.ticker || "").toUpperCase() === ticker)
          : null;

        const tickerRows = Array.isArray(tickerBlock?.rows) ? tickerBlock.rows : [];

        if (!cancelled) {
          setRows(tickerRows);
        }
      } catch (e) {
        if (!cancelled) {
          setError(String(e.message || e));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [ticker]);

  const sortedRows = useMemo(() => {
    return [...rows]
      .map((r) => ({
        date: r.date || "",
        return: r.return,
        macro: r.macro_component,
        cluster: r.cluster_component,
        idio: r.idiosyncratic_component,
        dominantDriver: r.dominant_driver || "",
        isIdioEvent: r.is_idio_event,
      }))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [rows]);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>
      <h2 style={{ margin: "0 0 12px 0" }}>
        {ticker || "Stock"} Return Decomposition
      </h2>

      {loading && <div>Loading…</div>}

      {!loading && error && (
        <div style={{ color: "crimson" }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && !ticker && (
        <div style={{ color: "#666" }}>
          No ticker found in route params.
        </div>
      )}

      {!loading && !error && ticker && sortedRows.length === 0 && (
        <div style={{ color: "#666" }}>
          No decomposition rows found for <strong>{ticker}</strong>.
        </div>
      )}

      {!loading && !error && sortedRows.length > 0 && (
        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 12,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Return</th>
                <th style={thStyle}>Macro</th>
                <th style={thStyle}>Cluster</th>
                <th style={thStyle}>Idiosyncratic</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, i) => {
                const highlight = r.isIdioEvent === 1;
                return (
                  <tr
                    key={`${r.date}-${i}`}
                    style={{
                      background: highlight ? "#fff7ed" : "#fff",
                    }}
                  >
                    <td style={tdStyle}>{r.date || "—"}</td>
                    <td style={{ ...tdStyle, color: getColor(r.return), fontWeight: 600 }}>
                      {formatPercent(r.return)}
                    </td>
                    <td style={{ ...tdStyle, color: getColor(r.macro) }}>
                      {formatPercent(r.macro)}
                    </td>
                    <td style={{ ...tdStyle, color: getColor(r.cluster) }}>
                      {formatPercent(r.cluster)}
                    </td>
                    <td style={{ ...tdStyle, color: getColor(r.idio), fontWeight: 700 }}>
                      {formatPercent(r.idio)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}